import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { getStories, mediaSrc } from "@/lib/api";

const PHOTO_MS = 5000;
const AVATAR = "/sean-avatar.jpg";

// Instagram-style story bubbles + full-screen viewer. Stories expire after 24h on the server.
export default function LabStories() {
  const [stories, setStories] = useState([]);
  const [openAt, setOpenAt] = useState(null);
  const [seen, setSeen] = useState(() => new Set(JSON.parse(localStorage.getItem("seanlab_seen_stories") || "[]")));

  useEffect(() => {
    let alive = true;
    const load = () => getStories().then((d) => { if (alive) setStories(d.items || []); }).catch(() => {});
    load();
    const id = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const markSeen = useCallback((id) => {
    setSeen((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev).add(id);
      localStorage.setItem("seanlab_seen_stories", JSON.stringify([...next].slice(-100)));
      return next;
    });
  }, []);

  if (!stories.length) return null;
  const firstUnseen = Math.max(0, stories.findIndex((s) => !seen.has(s.id)));

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-6" data-testid="lab-stories">
      <div className="flex items-center gap-4 overflow-x-auto pb-2">
        <StoryBubble
          img={AVATAR}
          label="Sean's story"
          unseen={stories.some((s) => !seen.has(s.id))}
          onClick={() => setOpenAt(firstUnseen)}
          testId="lab-stories-main-bubble"
        />
        {stories.map((s, i) => (
          <StoryBubble
            key={s.id}
            post={s}
            label={timeLeft(s.expires_at)}
            unseen={!seen.has(s.id)}
            onClick={() => setOpenAt(i)}
            testId={`lab-story-bubble-${i}`}
          />
        ))}
      </div>
      {openAt !== null && (
        <StoryViewer stories={stories} start={openAt} onSeen={markSeen} onClose={() => setOpenAt(null)} />
      )}
    </section>
  );
}

function StoryBubble({ img, post, label, unseen, onClick, testId }) {
  return (
    <button onClick={onClick} className="shrink-0 flex flex-col items-center gap-1 group" data-testid={testId}>
      <span
        className="p-[3px] rounded-full transition-transform group-hover:scale-105 group-active:scale-95"
        style={{ background: unseen ? "conic-gradient(var(--orange), var(--red), var(--sun), var(--orange))" : "#c9c4b8" }}
      >
        <span className="block w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full overflow-hidden" style={{ border: "3px solid var(--paper)" }}>
          {post ? <Thumb post={post} /> : <img src={img} alt="" className="w-full h-full object-cover" />}
        </span>
      </span>
      <span className="text-[0.65rem] font-bold text-[var(--ink)] max-w-[76px] truncate">{label}</span>
    </button>
  );
}

function Thumb({ post }) {
  const src = mediaSrc(post.upload_url);
  return post.upload_kind === "video"
    ? <video src={`${src}#t=0.1`} muted playsInline preload="metadata" className="w-full h-full object-cover" />
    : <img src={src} alt="" className="w-full h-full object-cover" />;
}

function StoryViewer({ stories, start, onSeen, onClose }) {
  const [i, setI] = useState(start);
  const [pct, setPct] = useState(0);
  const [muted, setMuted] = useState(false);
  const videoRef = useRef(null);
  const story = stories[i];

  const next = useCallback(() => (i < stories.length - 1 ? setI(i + 1) : onClose()), [i, stories.length, onClose]);
  const prev = () => setI(Math.max(0, i - 1));

  useEffect(() => { onSeen(story.id); setPct(0); }, [story.id, onSeen]);

  // Photo timer
  useEffect(() => {
    if (story.upload_kind === "video") return;
    const t0 = Date.now();
    const id = setInterval(() => {
      const p = ((Date.now() - t0) / PHOTO_MS) * 100;
      if (p >= 100) { clearInterval(id); next(); } else setPct(p);
    }, 50);
    return () => clearInterval(id);
  }, [story, next]);

  // Video: try with sound (visitor just tapped), fall back to muted if the browser refuses
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    v.play().catch(() => { v.muted = true; setMuted(true); v.play().catch(() => {}); });
  }, [story.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowRight") next(); if (e.key === "ArrowLeft") prev(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  });

  const src = mediaSrc(story.upload_url);
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center" style={{ background: "#0b0d1f" }} data-testid="story-viewer">
      <div className="relative w-full h-full sm:h-[92vh] sm:max-w-[420px] sm:rounded-2xl overflow-hidden bg-black">
        {story.upload_kind === "video" ? (
          <video
            key={story.id}
            ref={videoRef}
            src={src}
            playsInline
            autoPlay
            muted={muted}
            onTimeUpdate={(e) => setPct((e.target.currentTime / (e.target.duration || 1)) * 100)}
            onEnded={next}
            className="absolute inset-0 w-full h-full object-contain"
            data-testid="story-video"
          />
        ) : (
          <img key={story.id} src={src} alt={story.caption || "Sean's story"} className="absolute inset-0 w-full h-full object-contain" data-testid="story-image" />
        )}

        {/* progress bars */}
        <div className="absolute top-2 inset-x-2 flex gap-1 z-10">
          {stories.map((s, k) => (
            <div key={s.id} className="h-[3px] flex-1 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.35)" }}>
              <div className="h-full bg-white" style={{ width: k < i ? "100%" : k === i ? `${pct}%` : "0%" }} />
            </div>
          ))}
        </div>

        {/* header */}
        <div className="absolute top-5 inset-x-3 flex items-center gap-2 z-10 text-white">
          <img src={AVATAR} alt="" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
          <span className="font-bold text-sm">seanthesciencekid</span>
          <span className="text-xs opacity-70">{timeAgo(story.created_at)}</span>
          {story.upload_kind === "video" && muted && (
            <button onClick={() => { setMuted(false); if (videoRef.current) videoRef.current.muted = false; }} className="ml-auto text-xs font-bold px-2 py-1 rounded-full bg-white/20" data-testid="story-unmute-btn">
              🔊 sound
            </button>
          )}
          <button onClick={onClose} className={`${story.upload_kind === "video" && muted ? "" : "ml-auto"} p-1`} aria-label="Close story" data-testid="story-close-btn">
            <X size={22} />
          </button>
        </div>

        {/* tap zones */}
        <button className="absolute left-0 top-16 bottom-24 w-1/3 z-[5]" onClick={prev} aria-label="Previous story" data-testid="story-prev-zone" />
        <button className="absolute right-0 top-16 bottom-24 w-2/3 z-[5]" onClick={next} aria-label="Next story" data-testid="story-next-zone" />

        {story.caption && (
          <div className="absolute bottom-0 inset-x-0 p-4 pt-10 z-10 text-white text-sm" style={{ background: "linear-gradient(transparent, rgba(0,0,0,0.75))" }} data-testid="story-caption">
            {story.caption}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

function timeLeft(iso) {
  const h = Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 3600000));
  return h <= 1 ? "ends soon" : `${h}h left`;
}

function timeAgo(iso) {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h`;
}
